import type { LegendListProps } from '@legendapp/list/react-native';
import LegendList from '-/Custom/LegendList';
import { type ReactNode, useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import {
  UseEventListFragment,
  useEventList,
} from '@/components/Event/EventList.utils';
import { readFragment } from '@/gql/graphql';
import useRerenderMinutely from '@/hooks/useRerenderMinutely';
import Box from '../UI/Box';
import Button from '../UI/Button';
import Icon from '../Unistyles/Icon';

export type EventListProps = LegendListProps & {
  fetching: boolean;
  divideUpcoming?: boolean;
  data: readonly { id: string; datetime?: string | null }[];
  initialDate?: string;
  onPressScrollButton?: () => void;
  hideScrollButton?: boolean;
};

function ScrollButton({
  scrollDirection,
  onPress,
}: {
  scrollDirection: 'up' | 'down' | null;
  onPress: () => void;
}) {
  return scrollDirection ? (
    <Button
      iconOnly
      variant="secondary"
      style={style.scrollButton}
      onPress={onPress}
    >
      <Icon
        name={
          scrollDirection === 'up' ? 'arrow-up-outline' : 'arrow-down-outline'
        }
        variant="white"
      />
    </Button>
  ) : null;
}

export default function EventList({
  data,
  fetching,
  renderItem,
  divideUpcoming,
  initialDate,
  onEndReached,
  onStartReached,
  onViewableItemsChanged,
  onPressScrollButton,
  hideScrollButton,
  ...props
}: EventListProps) {
  const startReachedPendingRef = useRef(false);
  const endReachedPendingRef = useRef(false);
  const rerenderCount = useRerenderMinutely();

  const events = data?.map((event) =>
    readFragment(UseEventListFragment, event),
  );

  const helpers = useEventList({
    events,
    divideUpcoming,
    initialDate,
    fetching,
  });

  const initialScrollIndex = helpers.initialScrollIndex;
  const isLayoutDone = helpers.shouldRender;

  useEffect(() => {
    if (!fetching) {
      startReachedPendingRef.current = false;
      endReachedPendingRef.current = false;
    }
  }, [fetching]);

  if (
    !props.ListHeaderComponent &&
    props.ListEmptyComponent &&
    !fetching &&
    !data?.length
  ) {
    // TODO: remove casting
    return props.ListEmptyComponent as ReactNode;
  }

  return (
    <Box
      flex
      onLayout={helpers.container.onLayout}
      style={style.container}
      testID="EventListContainer"
    >
      {isLayoutDone ? (
        <LegendList
          {...(Platform.OS === 'web' ? {} : { scrollsToTop: false })}
          style={style.list}
          onViewableItemsChanged={(info) => {
            helpers.list.onViewableItemsChanged(info);
            onViewableItemsChanged?.(info);
          }}
          onEndReached={(info) => {
            if (!onEndReached || fetching || endReachedPendingRef.current) {
              return;
            }

            endReachedPendingRef.current = true;
            onEndReached(info);
          }}
          onStartReached={(info) => {
            if (!onStartReached || fetching || startReachedPendingRef.current) {
              return;
            }

            startReachedPendingRef.current = true;
            onStartReached(info);
          }}
          initialScrollIndex={initialScrollIndex}
          estimatedItemSize={120}
          recycleItems
          renderItem={renderItem}
          data={data}
          keyExtractor={helpers.list.keyExtractor}
          extraData={rerenderCount}
          {...props}
        />
      ) : null}

      {!hideScrollButton && onPressScrollButton ? (
        <ScrollButton {...helpers.scrollButton} onPress={onPressScrollButton} />
      ) : null}
    </Box>
  );
}

const style = StyleSheet.create((theme) => ({
  container: {
    backgroundColor: theme.colors.background,
  },
  list: {
    flex: 1,
  },
  scrollButton: {
    position: 'absolute',
    bottom: theme.size(8),
    alignSelf: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0.7,
    borderRadius: theme.borderRadius.full,
    width: theme.size(10),
    height: theme.size(10),
  },
}));
